import { useMemo, useState, useEffect, type ReactNode } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { api, ApiClientError } from '../lib/api';
import { latestAttr, registryOptions, humanizeToken, type AttrMap } from '../lib/inlineAttribution';
import { formatPhone } from '../lib/formatPhone';
import { Badge, Button, Input, IconPencil } from '../atoms';
import { InlineEditCard, CreateEntityModal, LastUpdated, useToast, type EntityContact } from '../organisms';
import { EntityHeader } from '../shell';
import { useEntityPermission } from '../lib/usePermissions';

/**
 * ContactDetail — /contacts/:id
 *
 * The profile of one PERSON. In Ignite a person belongs to a BUSINESS (the
 * `clients` table, which keeps its inherited name; the surface never says
 * "client"), and the reason to open this page is to see where that person sits
 * in the Supplier Journey: who they are, how to reach them, and what has
 * actually happened to them — referrals out to partners, programme enrollments,
 * sessions attended.
 *
 * Bespoke card profile: per-section InlineEditCards (edit-in-place → PUT
 * /api/contacts/:id) with per-section attribution from
 * GET /api/field-attribution/contacts/:id, plus a read-only Journey section.
 *
 * The WRINKLE (2.2): a person's phone/email do NOT live on the contacts row —
 * they sit in the polymorphic phone_numbers / email_addresses side tables and are
 * edited through the bespoke entity-contacts route, which is HOST-scoped
 * (:entityType/:entityId/:contactId). A standalone person page has no inherent
 * host, so the dedicated ContactMethodsCard discovers one from the person's
 * contact_list links (GET /api/contact-list?contact_id=<id>) and reads/writes the
 * methods through that host — never the generic contacts PUT, which can't reach
 * the side tables.
 */

interface Contact {
  id: string;
  first_name: string | null;
  last_name: string | null;
  company_name: string | null;
  courtesy: string | null;
  goes_by: string | null;
  type: string | null;
  status: 'active' | 'inactive';
  client_id: string | null;
  title: string | null;
  department: string | null;
  updated_at?: string | null;
  updated_by?: string | null;
}

// A row of the contact_list junction (GET /api/contact-list?contact_id=<id>).
interface ContactLink {
  id: string;
  entity_type: string;
  entity_id: string;
  contact_id: string;
  role: string | null;
  is_inherited?: number | boolean | null;
}

/**
 * The host entity_types the entity-contacts route can reach AND that still
 * exist in this product. `clients` (the business) is the only one: the
 * property-tax hosts this inherited (assessors, collectors, jurisdictions,
 * properties, parcels) have no Ignite equivalent and their tables are gone, so
 * asking contacts-by-entity for one would 500 rather than return contacts.
 */
const SUPPORTED_HOSTS = new Set(['clients']);

const contactName = (c: Contact): string =>
  [c.first_name, c.last_name].filter(Boolean).join(' ') || c.company_name || '—';

const statusVariant: Record<Contact['status'], 'success' | 'neutral'> = {
  active: 'success', inactive: 'neutral',
};

/**
 * Render a DATE column (`YYYY-MM-DD`, kept as a string by the pg type parser so
 * it carries no time-of-day and no zone). Parsed as UTC and printed as UTC so a
 * viewer west of Greenwich never sees the day before.
 */
function formatDay(value: unknown): string {
  if (value == null || value === '') return '—';
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(value));
  if (!m) return String(value);
  return new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3])))
    .toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric', timeZone: 'UTC' });
}

export function ContactDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [editAllOpen, setEditAllOpen] = useState(false);

  const contactQuery = useQuery<Contact>({
    queryKey: ['contacts', id],
    queryFn: ({ signal }) => api.get<Contact>(`/api/contacts/${id}`, signal),
    enabled: !!id,
  });

  // Gate the edit affordances on the contact grant (mirrors the server). Hook —
  // before any early return so hook order stays stable across load → loaded.
  const canEdit = useEntityPermission('contact', 'can_edit');

  if (contactQuery.isLoading) {
    return <p style={{ color: 'var(--color-ink-3)' }}>Loading person…</p>;
  }

  if (contactQuery.error) {
    const notFound = contactQuery.error instanceof ApiClientError && contactQuery.error.status === 404;
    return (
      <div style={{
        padding: 'var(--sp-5)',
        background: notFound ? 'var(--color-card)' : 'var(--color-status-red-bg)',
        border: '1px solid ' + (notFound ? 'var(--color-border)' : 'var(--color-status-red)'),
        color: notFound ? 'var(--color-ink)' : 'var(--color-status-red)',
        borderRadius: 'var(--r-lg)',
      }}>
        <h2 style={{ margin: '0 0 var(--sp-2)' }}>{notFound ? 'Person not found' : 'Failed to load person'}</h2>
        <p style={{ margin: 0 }}>{(contactQuery.error as Error).message}</p>
        <p style={{ marginTop: 'var(--sp-3)' }}>
          <Button variant="secondary" size="sm" onClick={() => navigate('/contacts')}>
            Back to People
          </Button>
        </p>
      </div>
    );
  }

  if (!contactQuery.data) return null;

  const contact = contactQuery.data;

  return (
    <>
      <EntityHeader
        prefix="Person"
        title={contactName(contact)}
        subtitle={<>
          {contact.goes_by ? <span>Known as {contact.goes_by}</span> : null}
          <LastUpdated updatedBy={contact.updated_by} updatedAt={contact.updated_at} />
        </>}
        statusPills={<>
          {contact.type && <Badge variant="neutral">{humanizeToken(contact.type)}</Badge>}
          <Badge variant={statusVariant[contact.status]} dot>{humanizeToken(contact.status)}</Badge>
        </>}
        actions={
          <>
            <Button variant="secondary" size="sm" onClick={() => navigate('/contacts')}>
              Back
            </Button>
            {canEdit && (
              // Fallback to the full-record form so person fields not surfaced in
              // the cards below stay reachable (mirrors ClientDetail).
              <Button variant="secondary" size="sm" onClick={() => setEditAllOpen(true)}>
                Edit all fields
              </Button>
            )}
          </>
        }
      />

      <OverviewCards contact={contact} canEdit={canEdit} />

      <JourneyPanel contactId={contact.id} />

      <CreateEntityModal
        open={editAllOpen}
        onClose={() => setEditAllOpen(false)}
        table="contacts"
        recordId={contact.id}
        invalidateKeys={[['contacts-list'], ['contacts', contact.id], ['field-attribution', 'contacts', contact.id]]}
      />
    </>
  );
}

function OverviewCards({ contact, canEdit }: { contact: Contact; canEdit: boolean }) {
  // Per-FIELD attribution so each box derives its own "last updated by · when".
  const attrQuery = useQuery<AttrMap>({
    queryKey: ['field-attribution', 'contacts', contact.id],
    queryFn: ({ signal }) => api.get<AttrMap>(`/api/field-attribution/contacts/${contact.id}`, signal),
  });
  const attr = attrQuery.data ?? {};
  const record = contact as unknown as Record<string, unknown>;
  const invalidate: unknown[][] = [['contacts', contact.id], ['contacts-list'], ['field-attribution', 'contacts', contact.id]];

  // Person type is an org-configurable registry (org-settings 'contact-types') —
  // same source the business-profile add-person form uses.
  const contactTypes = useQuery<string[] | { values: string[] }>({
    queryKey: ['org-settings', 'contact-types'],
    queryFn: ({ signal }) => api.get('/api/org-settings/contact-types', signal),
    staleTime: 5 * 60 * 1000,
  });

  // The person's BUSINESS (contacts.client_id → clients.name). The `clients`
  // table keeps its inherited name; the label never does. Read mode resolves the
  // id to the business NAME and links to its profile (never the raw id); edit
  // mode offers the same typeahead picker every other business picker uses.
  const businessesQuery = useQuery<{ id: string; name: string }[]>({
    queryKey: ['clients-list'],
    queryFn: ({ signal }) => api.get<{ id: string; name: string }[]>('/api/clients', signal),
    staleTime: 5 * 60 * 1000,
  });
  const businessOptions = (businessesQuery.data ?? []).map((c) => ({ value: c.id, label: c.name }));
  const businessNameById = new Map((businessesQuery.data ?? []).map((c) => [c.id, c.name] as const));

  const statusOptions = [
    { value: 'active', label: 'Active' },
    { value: 'inactive', label: 'Inactive' },
  ];

  return (
    <div style={{
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 280px), 1fr))',
      gap: 'var(--sp-4)',
    }}>
      <InlineEditCard
        title="Identity"
        routePath="contacts"
        recordId={contact.id}
        record={record}
        canEdit={canEdit}
        invalidateKeys={invalidate}
        attribution={latestAttr(attr, ['first_name', 'last_name', 'client_id', 'courtesy', 'goes_by', 'type', 'status'])}
        fields={[
          { key: 'first_name', label: 'First name' },
          { key: 'last_name', label: 'Last name' },
          {
            key: 'client_id',
            label: 'Business',
            type: 'searchable-select',
            options: businessOptions,
            display: (v) => {
              if (!v) return '—';
              const name = businessNameById.get(String(v));
              // '…' while the business list is still in flight — never the raw id.
              if (!name) return '…';
              return <Link to={`/clients/${v}`} style={{ color: 'var(--brand-primary)' }}>{name}</Link>;
            },
          },
          // contacts.courtesy holds the salutation (Mr./Ms./Dr.); contacts.goes_by
          // holds the name the person actually uses. Labelled for what they hold.
          { key: 'courtesy', label: 'Salutation' },
          { key: 'goes_by', label: 'Preferred name' },
          { key: 'type', label: 'Type', type: 'select', options: registryOptions(contactTypes.data), addOptionSlug: 'contact-types', display: (v) => (v ? humanizeToken(String(v)) : '—') },
          { key: 'status', label: 'Status', type: 'select', options: statusOptions, display: (v) => (v ? humanizeToken(String(v)) : '—') },
        ]}
      />

      <InlineEditCard
        title="Role at the business"
        routePath="contacts"
        recordId={contact.id}
        record={record}
        canEdit={canEdit}
        invalidateKeys={invalidate}
        attribution={latestAttr(attr, ['title', 'department'])}
        // `office` is deliberately absent: it was the consultancy's own office
        // location, which this product has no notion of. The column still exists
        // and "Edit all fields" still reaches it — this card just stops asking a
        // business owner to fill in a firm's office.
        fields={[
          { key: 'title', label: 'Title' },
          { key: 'department', label: 'Department' },
        ]}
      />

      <ContactMethodsCard contact={contact} canEdit={canEdit} />
    </div>
  );
}

/**
 * Pick a host link the entity-contacts route can reach. Only `clients` (the
 * business) is a reachable host, so this is "prefer a DIRECT link over an
 * inherited one". is_inherited comes back 0/1.
 */
function pickHost(links: ContactLink[]): ContactLink | null {
  const supported = links.filter((l) => SUPPORTED_HOSTS.has(l.entity_type));
  return supported.find((l) => !l.is_inherited) || supported[0] || null;
}

/**
 * ContactMethodsCard — the dedicated (non-InlineEditCard) box for a person's
 * primary email + phone, which live in the side tables the generic contacts PUT
 * can't reach. It discovers a host from the person's contact_list links and
 * reads/writes the methods through the bespoke entity-contacts route:
 *
 *   read : GET /api/contacts-by-entity/:entityType/:entityId  (host list; find me)
 *   save : PUT /api/entity-contacts/:entityType/:entityId/:contactId
 *
 * The save must carry the person's identity (first/last/company) because that
 * route rewrites the whole contact + its primary email/phone in one transaction;
 * omitting a name would blank it. Styled identically to InlineEditCard.
 */
function ContactMethodsCard({ contact, canEdit }: { contact: Contact; canEdit: boolean }) {
  const { toast } = useToast();
  const qc = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<{ email: string; phone: string }>({ email: '', phone: '' });

  // 1) The person's links → a reachable host. contact_id is a supported FK
  //    filter on the generic list, so this returns just this person's links.
  const linksQuery = useQuery<ContactLink[]>({
    queryKey: ['contact-links', contact.id],
    queryFn: ({ signal }) => api.get<ContactLink[]>(`/api/contact-list?contact_id=${contact.id}`, signal),
    enabled: !!contact.id,
  });
  const host = useMemo(() => pickHost(linksQuery.data ?? []), [linksQuery.data]);

  // 2) Read the person's primary email/phone from the host's contact list (the
  //    same enriched read EntityContactsPanel uses). The person is guaranteed to
  //    appear — the host was derived from one of its own links.
  const methodsQuery = useQuery<EntityContact[]>({
    queryKey: ['contact-methods', contact.id, host?.entity_type, host?.entity_id],
    queryFn: ({ signal }) => api.get<EntityContact[]>(`/api/contacts-by-entity/${host!.entity_type}/${host!.entity_id}`, signal),
    enabled: !!host,
  });
  const me = (methodsQuery.data ?? []).find((r) => r.id === contact.id) ?? null;
  const email = me?.email ?? null;
  const phone = me?.phone_number ?? null;

  const editable = canEdit && !!host;

  // Seed the draft each time we enter edit mode.
  useEffect(() => {
    if (!editing) return;
    setDraft({ email: email ?? '', phone: phone ?? '' });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editing]);

  const save = useMutation({
    mutationFn: () => {
      // Carry the person's identity so the whole-contact rewrite preserves it.
      const body: Record<string, string> = {
        first_name: contact.first_name ?? '',
        last_name: contact.last_name ?? '',
        company_name: contact.company_name ?? '',
        email: draft.email.trim(),
        phone: draft.phone.trim(),
      };
      return api.put(`/api/entity-contacts/${host!.entity_type}/${host!.entity_id}/${contact.id}`, body);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['contact-methods', contact.id] });
      qc.invalidateQueries({ queryKey: ['contact-links', contact.id] });
      qc.invalidateQueries({ queryKey: ['contacts', contact.id] });
      qc.invalidateQueries({ queryKey: ['field-attribution', 'contacts', contact.id] });
      toast({ variant: 'success', message: 'Contact methods updated.' });
      setEditing(false);
    },
    onError: (err) => toast({
      variant: 'error',
      message: err instanceof ApiClientError ? err.message : 'Could not update contact methods.',
    }),
  });

  return (
    <div style={{
      background: 'var(--color-card)', border: '1px solid var(--color-border)',
      borderRadius: 'var(--r-lg)', padding: 'var(--sp-4)', display: 'flex', flexDirection: 'column',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 'var(--sp-2)', marginBottom: 'var(--sp-3)' }}>
        <h3 style={{
          margin: 0, fontSize: 'var(--fs-xs)', fontWeight: 'var(--fw-semibold)',
          textTransform: 'uppercase', letterSpacing: 'var(--ls-wider)', color: 'var(--color-ink-3)',
        }}>Contact methods</h3>
        {editable && !editing && (
          <button
            type="button"
            onClick={() => setEditing(true)}
            aria-label="Edit Contact methods"
            title="Edit Contact methods"
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 4, border: 'none', background: 'transparent',
              cursor: 'pointer', color: 'var(--brand-primary)', fontSize: 'var(--fs-xs)',
              fontWeight: 'var(--fw-medium)', fontFamily: 'inherit', padding: 0,
            }}
          >
            <IconPencil size={13} /> Edit
          </button>
        )}
      </div>

      {editing ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-2)' }}>
          <label style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <span style={{ fontSize: 'var(--fs-xs)', color: 'var(--color-ink-3)' }}>Email</span>
            <Input
              type="email"
              value={draft.email}
              onChange={(e) => setDraft((d) => ({ ...d, email: e.target.value }))}
            />
          </label>
          <label style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <span style={{ fontSize: 'var(--fs-xs)', color: 'var(--color-ink-3)' }}>Phone</span>
            <Input
              inputMode="tel"
              placeholder="XXX-XXX-XXXX"
              value={draft.phone}
              onChange={(e) => setDraft((d) => ({ ...d, phone: formatPhone(e.target.value) }))}
            />
          </label>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 'var(--sp-2)', marginTop: 'var(--sp-2)' }}>
            <Button variant="secondary" size="sm" onClick={() => setEditing(false)} disabled={save.isPending}>Cancel</Button>
            <Button variant="primary" size="sm" loading={save.isPending} onClick={() => save.mutate()}>Save</Button>
          </div>
        </div>
      ) : (
        <div>
          <MethodRow label="Email" value={email ? <a href={`mailto:${email}`} style={{ color: 'var(--brand-primary)', overflowWrap: 'anywhere' }}>{email}</a> : '—'} />
          <MethodRow label="Phone" value={phone ? <a href={`tel:${phone}`} style={{ color: 'var(--brand-primary)' }}>{phone}</a> : '—'} />
          {!linksQuery.isLoading && !host && (
            <p style={{ margin: 'var(--sp-2) 0 0', fontSize: 'var(--fs-xs)', color: 'var(--color-ink-3)' }}>
              Contact methods are managed on the business this person is linked to.
            </p>
          )}
        </div>
      )}
    </div>
  );
}

function MethodRow({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div style={{
      display: 'flex', justifyContent: 'space-between', gap: 'var(--sp-3)',
      padding: 'var(--sp-1) 0', fontSize: 'var(--fs-sm)', borderBottom: '1px solid var(--color-border)',
    }}>
      <span style={{ color: 'var(--color-ink-3)', flexShrink: 0 }}>{label}</span>
      <span style={{ color: 'var(--color-ink)', fontWeight: 'var(--fw-medium)', textAlign: 'right', minWidth: 0, overflowWrap: 'anywhere' }}>{value}</span>
    </div>
  );
}

/* ------------------------------------------------------------------ *
 * Journey — Connect (referrals) · Develop (enrollments, attendance)
 * ------------------------------------------------------------------ */

// GET /api/referrals?contact_id=<id> — the generic list, bare array, LIMIT 500.
interface Referral {
  id: string;
  status: string | null;
  opened_date: string | null;
  capability_id: string | null;
  partner_id: string | null;
}

interface Enrollment {
  id: string;
  stage: string | null;
  role: string | null;
  program_id: string | null;
  application_date: string | null;
}

interface AttendanceRow {
  id: string;
  status: string | null;
  start_date: string | null;
}

const MAX_ROWS = 8;

/** Status → pill colour, tolerant of an unseen value (falls back to neutral). */
function tone(value: string | null | undefined): 'success' | 'warning' | 'info' | 'neutral' {
  const v = String(value ?? '').toLowerCase();
  if (v === 'attended' || v === 'completed' || v === 'closed' || v === 'graduated') return 'success';
  if (v === 'registered' || v === 'enrolled' || v === 'in_progress' || v === 'active') return 'info';
  if (v === 'no_show' || v === 'withdrawn' || v === 'declined') return 'warning';
  return 'neutral';
}

/**
 * JourneyPanel — what has actually happened to this person, read-only.
 *
 * Every stream is gated on its OWN entity grant, so a role that may see a person
 * but not the referral book gets that card omitted rather than an error. The
 * `attendance` table is governed by the program_session grid (the table→entity
 * map in src/middleware/auth.js), not an 'attendance' entity of its own.
 */
function JourneyPanel({ contactId }: { contactId: string }) {
  const canSeeReferrals = useEntityPermission('referral', 'can_view');
  const canSeeEnrollments = useEntityPermission('enrollment', 'can_view');
  const canSeeAttendance = useEntityPermission('program_session', 'can_view');

  const referralsQuery = useQuery<Referral[]>({
    queryKey: ['contact-referrals', contactId],
    queryFn: ({ signal }) => api.get<Referral[]>(`/api/referrals?contact_id=${contactId}`, signal),
    enabled: canSeeReferrals,
  });
  const enrollmentsQuery = useQuery<Enrollment[]>({
    queryKey: ['contact-enrollments', contactId],
    queryFn: ({ signal }) => api.get<Enrollment[]>(`/api/enrollments?contact_id=${contactId}`, signal),
    enabled: canSeeEnrollments,
  });
  const attendanceQuery = useQuery<AttendanceRow[]>({
    queryKey: ['contact-attendance', contactId],
    queryFn: ({ signal }) => api.get<AttendanceRow[]>(`/api/attendance?contact_id=${contactId}`, signal),
    enabled: canSeeAttendance,
  });

  const referrals = referralsQuery.data ?? [];
  const enrollments = enrollmentsQuery.data ?? [];
  const attendance = attendanceQuery.data ?? [];

  // The two taxonomies a referral names, and the programme catalogue. All three
  // are small reference tables (tens of rows) — fetched only once a row needs
  // one, and cached across the session.
  const capabilitiesQuery = useQuery<{ id: string; label: string }[]>({
    queryKey: ['capabilities-list'],
    queryFn: ({ signal }) => api.get<{ id: string; label: string }[]>('/api/capabilities', signal),
    staleTime: 5 * 60 * 1000,
    enabled: referrals.length > 0,
  });
  const partnersQuery = useQuery<{ id: string; name: string }[]>({
    queryKey: ['partners-list'],
    queryFn: ({ signal }) => api.get<{ id: string; name: string }[]>('/api/partners', signal),
    staleTime: 5 * 60 * 1000,
    enabled: referrals.length > 0,
  });
  const programsQuery = useQuery<{ id: string; name: string }[]>({
    queryKey: ['programs-list'],
    queryFn: ({ signal }) => api.get<{ id: string; name: string }[]>('/api/programs', signal),
    staleTime: 5 * 60 * 1000,
    enabled: enrollments.length > 0,
  });

  const capabilityById = useMemo(
    () => new Map((capabilitiesQuery.data ?? []).map((c) => [c.id, c.label] as const)),
    [capabilitiesQuery.data],
  );
  const partnerById = useMemo(
    () => new Map((partnersQuery.data ?? []).map((p) => [p.id, p.name] as const)),
    [partnersQuery.data],
  );
  const programById = useMemo(
    () => new Map((programsQuery.data ?? []).map((p) => [p.id, p.name] as const)),
    [programsQuery.data],
  );

  // Attendance rows carry no session title in the migrated data (every
  // program_session_id is null), so listing them one by one would print a column
  // of blanks. The honest read is the tally plus the span it covers.
  const attendanceDays = attendance.map((a) => a.start_date).filter(Boolean).sort() as string[];
  const attendanceByStatus = attendance.reduce<Record<string, number>>((acc, a) => {
    const k = a.status ? humanizeToken(a.status) : 'Unrecorded';
    acc[k] = (acc[k] ?? 0) + 1;
    return acc;
  }, {});

  // Nothing this viewer is allowed to see → no section at all, rather than three
  // boxes that all say "none" for a reason that is a permission, not a fact.
  if (!canSeeReferrals && !canSeeEnrollments && !canSeeAttendance) return null;

  return (
    <section style={{ marginTop: 'var(--sp-6)' }}>
      <h2 style={{
        margin: '0 0 var(--sp-1)', fontSize: 'var(--fs-xl)',
        fontWeight: 'var(--fw-semibold)', color: 'var(--color-ink)',
      }}>Journey</h2>
      <p style={{ margin: '0 0 var(--sp-3)', fontSize: 'var(--fs-sm)', color: 'var(--color-ink-3)' }}>
        Where this person has been routed and what they have taken part in.
      </p>
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 280px), 1fr))',
        gap: 'var(--sp-4)',
      }}>
        {canSeeReferrals && (
          <JourneyCard
            title="Referrals"
            count={referrals.length}
            loading={referralsQuery.isLoading}
            empty="No partner referrals recorded."
          >
            {referrals.slice(0, MAX_ROWS).map((r) => (
              <JourneyRow
                key={r.id}
                to={`/referrals/${r.id}`}
                primary={(r.partner_id && partnerById.get(r.partner_id)) || 'Partner not named'}
                secondary={(r.capability_id && capabilityById.get(r.capability_id)) || 'Capability not recorded'}
                meta={formatDay(r.opened_date)}
                status={r.status}
              />
            ))}
          </JourneyCard>
        )}

        {canSeeEnrollments && (
          <JourneyCard
            title="Enrollments"
            count={enrollments.length}
            loading={enrollmentsQuery.isLoading}
            empty="Not enrolled in a programme."
          >
            {enrollments.slice(0, MAX_ROWS).map((e) => (
              <JourneyRow
                key={e.id}
                to={`/enrollments/${e.id}`}
                primary={(e.program_id && programById.get(e.program_id)) || 'Programme not named'}
                secondary={e.role ? `as ${humanizeToken(e.role)}` : null}
                meta={formatDay(e.application_date)}
                status={e.stage}
              />
            ))}
          </JourneyCard>
        )}

        {canSeeAttendance && (
          <JourneyCard
            title="Attendance"
            count={attendance.length}
            loading={attendanceQuery.isLoading}
            empty="No sessions attended."
          >
            {Object.entries(attendanceByStatus).map(([label, n]) => (
              <div key={label} style={{
                display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 'var(--sp-3)',
                padding: 'var(--sp-1) 0', fontSize: 'var(--fs-sm)', borderBottom: '1px solid var(--color-border)',
              }}>
                <Badge variant={tone(label)}>{label}</Badge>
                <span style={{ color: 'var(--color-ink)', fontWeight: 'var(--fw-medium)' }}>{n}</span>
              </div>
            ))}
            {attendanceDays.length > 0 && (
              <p style={{ margin: 'var(--sp-2) 0 0', fontSize: 'var(--fs-xs)', color: 'var(--color-ink-3)' }}>
                {formatDay(attendanceDays[0])} – {formatDay(attendanceDays[attendanceDays.length - 1])}
              </p>
            )}
          </JourneyCard>
        )}
      </div>
    </section>
  );
}

/** One box of the Journey grid: a count in the header, rows, or an honest empty line. */
function JourneyCard({ title, count, loading, empty, children }: {
  title: string;
  count: number;
  loading: boolean;
  empty: string;
  children: ReactNode;
}) {
  return (
    <div style={{
      background: 'var(--color-card)', border: '1px solid var(--color-border)',
      borderRadius: 'var(--r-lg)', padding: 'var(--sp-4)', display: 'flex', flexDirection: 'column',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 'var(--sp-2)', marginBottom: 'var(--sp-3)' }}>
        <h3 style={{
          margin: 0, fontSize: 'var(--fs-xs)', fontWeight: 'var(--fw-semibold)',
          textTransform: 'uppercase', letterSpacing: 'var(--ls-wider)', color: 'var(--color-ink-3)',
        }}>{title}</h3>
        {!loading && count > 0 && (
          <span style={{ fontSize: 'var(--fs-sm)', fontWeight: 'var(--fw-semibold)', color: 'var(--color-ink)' }}>{count}</span>
        )}
      </div>
      {loading ? (
        <p style={{ margin: 0, fontSize: 'var(--fs-sm)', color: 'var(--color-ink-3)' }}>Loading…</p>
      ) : count === 0 ? (
        <p style={{ margin: 0, fontSize: 'var(--fs-sm)', color: 'var(--color-ink-3)' }}>{empty}</p>
      ) : (
        <>
          {children}
          {count > MAX_ROWS && (
            <p style={{ margin: 'var(--sp-2) 0 0', fontSize: 'var(--fs-xs)', color: 'var(--color-ink-3)' }}>
              Showing {MAX_ROWS} of {count}.
            </p>
          )}
        </>
      )}
    </div>
  );
}

/** One referral / enrollment line, linked to its own record. */
function JourneyRow({ to, primary, secondary, meta, status }: {
  to: string;
  primary: string;
  secondary: string | null;
  meta: string;
  status: string | null;
}) {
  return (
    <div style={{
      display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 'var(--sp-3)',
      padding: 'var(--sp-1) 0', fontSize: 'var(--fs-sm)', borderBottom: '1px solid var(--color-border)',
    }}>
      <span style={{ minWidth: 0, overflowWrap: 'anywhere' }}>
        <Link to={to} style={{ color: 'var(--brand-primary)', fontWeight: 'var(--fw-medium)' }}>{primary}</Link>
        {secondary && (
          <span style={{ display: 'block', fontSize: 'var(--fs-xs)', color: 'var(--color-ink-3)' }}>{secondary}</span>
        )}
      </span>
      <span style={{ flexShrink: 0, textAlign: 'right', display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 2 }}>
        {status && <Badge variant={tone(status)}>{humanizeToken(status)}</Badge>}
        <span style={{ fontSize: 'var(--fs-xs)', color: 'var(--color-ink-3)' }}>{meta}</span>
      </span>
    </div>
  );
}
